import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../../hooks/useLanguage'
import { UserIcon, ShieldIcon, ChevronDownIcon } from '../icons/Icons'

interface LoginDropdownProps {
  className?: string
}

const LoginDropdown: React.FC<LoginDropdownProps> = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { t } = useLanguage()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLoginClick = (role: 'citizen' | 'official') => {
    setIsOpen(false)
    navigate(`/login?role=${role}`)
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span>{t('auth.selectRole')}</span>
        <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          <button
            onClick={() => handleLoginClick('citizen')}
            className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex-shrink-0">
              <UserIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="font-medium text-gray-900">{t('auth.loginAsCitizen')}</div>
              <div className="text-sm text-gray-500">Report and track civic issues</div>
            </div>
          </button>

          <button
            onClick={() => handleLoginClick('official')}
            className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex-shrink-0">
              <ShieldIcon className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="font-medium text-gray-900">{t('auth.loginAsOfficial')}</div>
              <div className="text-sm text-gray-500">Manage and resolve issues</div>
            </div>
          </button>

          <div className="border-t border-gray-100 mt-2 pt-2">
            <button
              onClick={() => {
                setIsOpen(false)
                navigate('/browse')
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
            >
              {t('auth.continueAsGuest')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default LoginDropdown
